import { z } from 'zod';
import { ServiceIdSchema } from './common';
import { LocaleSchema } from './specialization';

/**
 * POST /api/v1/matching/find
 *
 * All criteria are optional and soft — a missing criterion never excludes a
 * therapist, it just doesn't contribute to their score (see
 * services/api/src/modules/matching/matching.service.ts for the weights).
 * The only hard filters are server-side: TherapistProfile.status === 'APPROVED'
 * and the owning User not soft-deleted.
 */
/**
 * Psychologist vs counsellor/coach — mirrors Prisma's `ProfessionalType`.
 * Lives here rather than being imported from the API package so the frontends
 * can use it without depending on Prisma.
 */
export const ProfessionalTypeSchema = z.enum(['PSYCHOLOGIST', 'COUNSELOR']);
export type ProfessionalType = z.infer<typeof ProfessionalTypeSchema>;

export const MatchingCriteriaSchema = z.object({
  specializationSlugs: z.array(z.string().min(1).max(60)).max(10).optional(),
  /**
   * Hard filter, unlike every other criterion here: a client looking for a
   * licensed psychologist must never be scored into a coach. Defaults to
   * PSYCHOLOGIST so that a caller who forgets it gets the conservative list.
   */
  professionalType: ProfessionalTypeSchema.default('PSYCHOLOGIST'),
  /** ISO 639-1, e.g. ["el", "en"]. */
  languages: z.array(z.string().min(2).max(5)).max(5).optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
  therapyStyle: z.string().min(1).max(60).optional(),
  /** ISO-3166 alpha-2, e.g. "GR". */
  country: z.string().length(2).optional(),
  /** Client's budget ceiling per session, in EUR. */
  maxPrice: z.coerce.number().positive().max(10000).optional(),
  minExperienceYears: z.coerce.number().int().min(0).max(60).optional(),
  /** Locale for translated specialization names in the response. */
  locale: LocaleSchema.default('el'),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type MatchingCriteria = z.infer<typeof MatchingCriteriaSchema>;

export const MatchScoreBreakdownSchema = z.object({
  specialization: z.number(),
  language: z.number(),
  price: z.number(),
  gender: z.number(),
  therapyStyle: z.number(),
  country: z.number(),
  experience: z.number(),
  rating: z.number(),
});
export type MatchScoreBreakdown = z.infer<typeof MatchScoreBreakdownSchema>;

export const MatchedSpecializationSchema = z.object({
  slug: z.string(),
  name: z.string(),
});

export const MatchedTherapistServiceSchema = z.object({
  serviceId: ServiceIdSchema,
  name: z.string(),
  durationMinutes: z.number().int().positive(),
  price: z.string(),   // Decimal serialized as string, e.g. "60.00"
  currency: z.string(),
});
export type MatchedTherapistService = z.infer<typeof MatchedTherapistServiceSchema>;

export const MatchedTherapistSchema = z.object({
  therapistProfileId: z.string().cuid(),
  /** User.id — this is what CreateBookingSchema.therapistId expects, not therapistProfileId. */
  therapistUserId: z.string().cuid(),
  displayName: z.string().nullable(),
  /** Resolved for `criteria.locale`, falling back to Greek if the therapist hasn't translated their profile yet (ADR-015 §6) — never null just because a translation is missing. */
  headline: z.string().nullable(),
  bio: z.string().nullable(),
  /** True when `criteria.locale` was `en` but the therapist has no English version yet, so the Greek text was shown instead — render a "not yet translated" hint, never a blank card. Always false when `criteria.locale` is `el`. */
  profileTranslationMissing: z.boolean(),
  hourlyRate: z.string(),   // Decimal serialized as string, e.g. "60.00"
  currency: z.string(),
  languagesSpoken: z.array(z.string()),
  gender: z.string().nullable(),
  yearsOfExperience: z.number().int().nullable(),
  therapyStyle: z.string().nullable(),
  country: z.string().nullable(),
  ratingAverage: z.number(),
  reviewCount: z.number().int(),
  /** Public profile photo URL (served via GET /api/v1/media/:id/raw) — null until one is set. */
  photoUrl: z.string().nullable(),
  /** Optional self-presentation video link (YouTube/Vimeo/etc.). */
  introVideoUrl: z.string().nullable(),
  /**
   * Licensed psychologist vs counsellor/coach. The public page claims "only
   * licensed psychologists", so this must travel with every card rather than
   * being inferred client-side — a card with no type is not a psychologist
   * card by default, it is an unverified one.
   */
  professionalType: ProfessionalTypeSchema,
  /** Free-form "περιστατικά" chips, distinct from the specialization taxonomy. Resolved for `criteria.locale` same as headline/bio. */
  caseTags: z.array(z.string()),
  specializations: z.array(MatchedSpecializationSchema),
  services: z.array(MatchedTherapistServiceSchema),
  score: z.number().min(0).max(100),
  scoreBreakdown: MatchScoreBreakdownSchema,
});
export type MatchedTherapist = z.infer<typeof MatchedTherapistSchema>;

export const MatchingFindResponseSchema = z.object({
  results: z.array(MatchedTherapistSchema),
  total: z.number().int(),
});
export type MatchingFindResponse = z.infer<typeof MatchingFindResponseSchema>;
